import { useEffect } from 'react';
import { GraphicsLayer, MapView } from '../constructors';
import {
    createCircleGraphic,
    createEntityIconGraphic,
    createEntityLabelGraphic,
    createPolygonGraphic,
    LabelFont,
} from '../helpers/graphics';
import { convertPointsToCoordinates } from '../helpers/polygons';
import { convertFeetToMeters } from '../../common/helpers/utils';
import type { GeoPoint } from '../../location/types';
import { ESRI_MAP_DEFAULT_ZOOM_LEVEL } from '../configurations';

/**
 * render a single graphic on map.
 * NOTE: only one graphic will be displayed, all other graphics will be removed.
 * use case: e.g. show a polygon on an embedded esri map
 */

export const useSingleGraphicDisplayLayer = ({
    mapView,
    center,
    radiusInFeet,
    points,
    icon,
    label,
}: {
    mapView?: MapView;
    center?: GeoPoint;
    radiusInFeet?: number;
    points?: GeoPoint[];
    icon?: { url: string; width?: string; height?: string; color?: string };
    label?: { text: string; font: LabelFont; yoffset?: number };
}) => {
    useEffect(
        function addCircleToMap() {
            if (mapView && center && !!radiusInFeet && radiusInFeet >= 0) {
                // remove all graphics
                mapView.map.removeAll();

                const circleGraphic = createCircleGraphic({
                    center,
                    radius: convertFeetToMeters(radiusInFeet),
                });
                if (circleGraphic) {
                    const graphicsLayer = new GraphicsLayer();
                    graphicsLayer.add(circleGraphic);
                    mapView?.map.add(graphicsLayer);

                    // zoom map to fit the circle
                    mapView
                        ?.goTo(circleGraphic.geometry.extent.expand(1), { animate: false })
                        .catch(() => {});
                }
            }
        },
        [mapView, center, radiusInFeet]
    );

    useEffect(
        function addPolygonToMap() {
            if (mapView && points?.length) {
                // remove all graphics
                mapView.map.removeAll();

                const coordinates = points ? convertPointsToCoordinates(points) : [];
                const polygonGraphic = createPolygonGraphic({
                    coordinates,
                });

                if (polygonGraphic) {
                    const graphicsLayer = new GraphicsLayer();
                    graphicsLayer.add(polygonGraphic);
                    mapView.map.add(graphicsLayer);

                    // zoom map to fit the polygon
                    mapView
                        .goTo(polygonGraphic.geometry.extent.expand(1), { animate: false })
                        .catch(() => {});
                }
            }
        },
        [mapView, points]
    );

    useEffect(
        function addEntityIconOrLabelToMap() {
            if (mapView && center && (icon || label)) {
                // remove all graphics
                mapView.map.removeAll();

                const graphicsLayer = new GraphicsLayer();

                if (icon) {
                    const iconGraphic = createEntityIconGraphic({
                        center,
                        url: icon.url,
                        width: icon.width,
                        height: icon.height,
                        color: icon.color,
                    });
                    if (iconGraphic) {
                        graphicsLayer.add(iconGraphic);
                    }
                }
                if (label) {
                    const labelGraphic = createEntityLabelGraphic({
                        center,
                        label: label.text,
                        font: label.font,
                        yoffset: label.yoffset,
                    });
                    if (labelGraphic) {
                        graphicsLayer.add(labelGraphic);
                    }
                }
                mapView?.map.add(graphicsLayer);
                mapView
                    ?.goTo(
                        {
                            center: [center.longitude, center.latitude],
                            zoom: ESRI_MAP_DEFAULT_ZOOM_LEVEL,
                        },
                        { animate: false }
                    )
                    .catch(() => {});
            }
        },
        [mapView, center, icon, label]
    );
};
